import Rating from "@/components/Rating";
import VLFTextField from "@/components/Input/TextField";
import {ATTITUDES} from "@/utils/scoutingData";
import {countWords, VERDICTS} from "@/utils/scoutingReports";
import {REQUIRED_FIELD} from "@/utils/constants";

import "./index.scss";

const toggle = (list, id) =>
	(list || []).includes(id)
		? list.filter((x) => x !== id)
		: [...(list || []), id];

function Chips({options, value, multiple, onChange, label}) {
	const selected = multiple ? value || [] : value ? [value] : [];
	return (
		<div className="srf-chips" role="group" aria-label={label}>
			{options.map((o) => {
				const active = selected.includes(o.id);
				return (
					<button
						key={o.id}
						type="button"
						className={"srf-chip" + (active ? " is-active" : "")}
						style={o.color ? {"--chip-color": o.color} : undefined}
						aria-pressed={active}
						onClick={() =>
							onChange(multiple ? toggle(value, o.id) : active ? null : o.id)
						}
					>
						{o.label}
					</button>
				);
			})}
		</div>
	);
}

function FieldInput({field, value, onChange}) {
	switch (field.type) {
		case "score":
			return (
				<Rating
					min={1}
					max={5}
					step={1}
					selectedRating={value ?? null}
					setSelectedRating={onChange}
				/>
			);
		case "attitude":
			return (
				<Chips
					label={field.label}
					options={ATTITUDES.map((a) => ({
						id: a.id,
						label: a.id + " · " + a.label,
						color: a.color,
					}))}
					value={value}
					multiple
					onChange={onChange}
				/>
			);
		case "choice":
			return (
				<Chips
					label={field.label}
					options={(field.options || []).map((o) => ({id: o, label: o}))}
					value={value}
					multiple={!!field.multiple}
					onChange={onChange}
				/>
			);
		default:
			return (
				<VLFTextField
					value={value || ""}
					onChange={(e) => onChange(e.target.value)}
					placeholder={field.label}
					multiline
					minRows={3}
					maxRows={12}
					size="small"
					inputProps={{"aria-label": field.label}}
				/>
			);
	}
}

const FieldLabel = ({label, required}) => (
	<div className="field-label-container">
		<div className="field-label">{label}</div>
		{required && <div className="field-required">*</div>}
	</div>
);

// Compilazione di una relazione sulla copia del template; il blocco giudizio è sempre in fondo.
export default function ScoutReportForm({report, onChange, errors = []}) {
	const fields = report?.template?.fields || [];
	const values = report?.values || {};
	const words = countWords(report?.judgment);
	const setValue = (id, value) =>
		onChange({...report, values: {...values, [id]: value}});

	return (
		<div className="scout-report-form">
			{fields.map((field) => (
				<div
					key={field.id}
					className={
						"srf-field srf-field--" +
						field.type +
						(errors.includes(field.id) ? " has-error" : "")
					}
				>
					<FieldLabel label={field.label} required={field.required} />
					<FieldInput
						field={field}
						value={values[field.id]}
						onChange={(value) => setValue(field.id, value)}
					/>
					{errors.includes(field.id) && (
						<div className="srf-error">{REQUIRED_FIELD}</div>
					)}
				</div>
			))}

			<section className="srf-judgment" aria-label="Giudizio">
				<FieldLabel label="Verdetto" required />
				<div className="srf-verdicts" role="group" aria-label="Verdetto">
					{VERDICTS.map((v) => (
						<button
							key={v.id}
							type="button"
							className={
								"srf-verdict" + (report?.verdict === v.id ? " is-active" : "")
							}
							style={{"--verdict-color": v.color, "--verdict-bg": v.bg}}
							aria-pressed={report?.verdict === v.id}
							onClick={() => onChange({...report, verdict: v.id})}
						>
							{v.label}
						</button>
					))}
				</div>
				{errors.includes("verdict") && (
					<div className="srf-error">{REQUIRED_FIELD}</div>
				)}
				<VLFTextField
					label="Giudizio testuale"
					required
					placeholder="Sintesi motivata del verdetto"
					value={report?.judgment || ""}
					onChange={(e) => onChange({...report, judgment: e.target.value})}
					multiline
					minRows={4}
					maxRows={16}
					size="small"
					inputProps={{"aria-label": "Giudizio testuale"}}
					error={errors.includes("judgment")}
					helperText={
						errors.includes("judgment")
							? REQUIRED_FIELD
							: words + (words === 1 ? " parola" : " parole")
					}
				/>
			</section>
		</div>
	);
}
